/**
 * Тексты юридических документов.
 *
 * ЗАЧЕМ ОТДЕЛЬНЫМ ФАЙЛОМ, а не в общих переводах: документы длинные, правятся
 * целыми абзацами и живут своей жизнью — держать их среди подписей кнопок
 * значит превратить файл переводов в нечитаемый.
 *
 * ИСТОЧНИК (25.09.2026, сборка 61). Политика и условия — `docs/refs/app-legal-2026-09-22/`
 * (`privacy-ru/en.md`, `terms-ru/en.md`, разбор по коду — `README.md` там же). Эти же тексты
 * владелец выкладывает в Google Docs; две редакции одного документа не держим — правка идёт
 * сюда и туда одновременно. Казахские версии переведены 25.09 здесь — проверить методисту.
 *
 * ⚠️ ЭТО НЕ ЮРИДИЧЕСКОЕ ЗАКЛЮЧЕНИЕ. Текст описывает фактическое устройство
 * платформы — что собирается, где хранится, что уходит за пределы страны. Основания
 * обработки и возраст по закону РК должен посмотреть юрист.
 *
 * ⚠️ УДАЛЕНИЕ АККАУНТА — ТОЛЬКО ПРАВДА (D71, решение владельца 23.09.2026). Кнопка в
 * профиле ЗАКРЫВАЕТ учётную запись (status «deleted», строки остаются), стирание данных —
 * по письму, срока не обещаем: задания, которое стирает данные, нет. Каждое утверждение
 * страницы удаления сверено с кодом 25.09.2026:
 *  - вход закрывается сразу: вход паролем и кодом у неактивной учётки — отказ
 *    (`auth-service/internal/service/auth_service.go` Login, `login_code.go` VerifyLoginCode);
 *  - сеансы на всех устройствах: продления гасит триггер `000022_session_cutoff` в той же
 *    транзакции, открытые экраны отсекает `/auth/validate` — кэш статуса не дольше 60 с
 *    (`account_status.go`, accountStatusTTL), отсюда «не позже чем через минуту»;
 *  - новая учётка на ту же почту — отказ регистрации (`auth_service.go`, ErrAccountRemoved);
 *  - вернуть можно: администратор восстанавливает учётку вместе с записями на курсы
 *    (`users-service/internal/service/account_removal.go` RestoreUser).
 * «30 дней», «обезличим», «необратимо» — запрещены сторожем `legalTruth.test.ts`. Появится
 * настоящее стирание — формулировка готова в README разбора, §3а.
 *
 * ⚠️ КНОПКА «УДАЛИТЬ ПРОФИЛЬ» — ТОЛЬКО В ПРИЛОЖЕНИИ (D37, решение владельца
 * 12.09.2026). На сайте она скрыта флагом `show_self_delete` (`lib/selfDelete.ts`),
 * в приложении из магазина есть всегда (Apple 5.1.1(v)). С сайта учётную запись
 * закрывают по письму. Вернут кнопку на сайт — вернуть «и на сайте» во все тексты.
 *
 * ГЛАВНОЕ, ЧТО НЕЛЬЗЯ ЗАМАЛЧИВАТЬ: часть данных уходит в США — OpenAI (тексты работ,
 * расшифровки, живой голос, слова словаря), ElevenLabs (текст слова), Resend (письма).
 * Локализация хранилищ в Казахстане этот канал не закрывает.
 */

import { absoluteLegalUrl, DELETE_ACCOUNT_URL, PRIVACY_URL } from '@/lib/legalLinks';
import { APP_BRAND, IS_APP_BUILD } from '@/lib/appBrand';

export type LegalSection = { title: string; body: string[] };
export type LegalDoc = { title: string; updated: string; intro: string; sections: LegalSection[] };

/**
 * РЕКВИЗИТЫ ОПЕРАТОРА — ОДНИМ МЕСТОМ (решение владельца 25.09.2026).
 *
 * Сейчас здесь реквизиты, которые стоят на сайте. Владелец выбирает оператора (ИП — ИИН,
 * ТОО — БИН) и меняет эти три строки — больше нигде реквизиты не пишутся, все документы и
 * все языки берут их отсюда (сверяет `legalTexts.test.ts`). Меток вида `{{…}}` сюда не
 * ставить: `scripts/release-ios.sh` не пропустит такую сборку в магазин.
 */
const REQUISITES = {
  ru: 'Индивидуальный предприниматель «ONE BILIM ACADEMY», ИИН 030520501856, Республика Казахстан, город Астана, проспект Туран, дом 43В, кв/офис 161',
  kk: '«ONE BILIM ACADEMY» жеке кәсіпкері, ЖСН 030520501856, Қазақстан Республикасы, Астана қаласы, Тұран даңғылы, 43В үй, пәтер/кеңсе 161',
  en: 'Individual entrepreneur ONE BILIM ACADEMY, IIN 030520501856, 43V Turan Avenue, apt./office 161, Astana, Republic of Kazakhstan',
} as const;

const CONTACT = 'support@1bilimcompany.com';
const UPDATED = '25.09.2026';

/** Ссылка на политику внутри условий и на страницу удаления внутри политики — полным адресом. */
const PRIVACY_LINK = absoluteLegalUrl(PRIVACY_URL);
const DELETE_LINK = absoluteLegalUrl(DELETE_ACCOUNT_URL);

/**
 * Название сервиса и приложения в документах. В store-сборке — APP_BRAND: с 1.1.1 OneBilim Academy (ребрендинг,
 * 05.10.2026; в 61 и 102 по D76 было «1 BILIM ACADEMY»). Те же слова — в Google Docs политики и условий: ревьюер
 * сверяет одно с другим, Docs владелец правит до подачи. Дату редакции UPDATED здесь не поднимали: она общая с сайтом
 * (техдолг, ребрендинг 1.1.1). На сайте — прежние Onebilim (сервис) и OneBilim (приложение): сайт не трогаем.
 * Домен academy.onebilim.kz и почта — не бренд, они в текстах остаются. Тернарник, а не isStoreBuild(): ветка сайта
 * выпадает из dist-mobile при сборке (lib/appBrand.ts).
 */
const SERVICE = IS_APP_BUILD ? APP_BRAND : 'Onebilim';
const APP_NAME = IS_APP_BUILD ? APP_BRAND : 'OneBilim';

/*
 * ⚠️ РЕДАКЦИЯ 14.09.2026 (решение владельца, Apple 3.1.3(f)) ОСТАЁТСЯ В СИЛЕ: ничего платного
 * нигде — ни оплаты, ни оферты, ни возврата, ни менеджера; «доступ предоставляет школа» и «срок
 * доступа» запрещены (D59). Сторож `app/noSellingSource.test.ts` читает этот файл.
 */

/* ─────────────────── политика конфиденциальности ─────────────────── */

const privacyRu: LegalDoc = {
  title: 'Политика конфиденциальности',
  updated: `Редакция от ${UPDATED}`,
  intro: `Эта политика объясняет, какие данные о вас собирает сервис ${SERVICE}, зачем, где они хранятся, сколько и кому передаются. Сервис — это приложение ${APP_NAME} и веб-версия academy.onebilim.kz: у них одна учётная запись. Оператор данных — ${REQUISITES.ru}. Связаться с нами: ${CONTACT}. Мы ничего не взимаем за приложение и его материалы, в приложении ничего не продаётся, и поэтому мы не получаем и не храним никаких банковских данных.`,
  sections: [
    {
      title: '1. Какие данные мы собираем',
      body: [
        'То, что вы указываете сами:',
        '• имя и адрес электронной почты — при регистрации;',
        '• номер телефона — по желанию;',
        '• изображение профиля — если вы его загрузите;',
        '• язык интерфейса и часовой пояс.',
        'То, что появляется, пока вы учитесь:',
        '• прогресс: какие уроки пройдены, баллы, результаты заданий;',
        '• ваши ответы на задания, в том числе тексты;',
        '• записи голоса — в заданиях, где нужно ответить устно;',
        '• фотографии и файлы, которые вы прикладываете к заданиям;',
        '• слова, которые вы добавили в словарь, и история их повторения;',
        '• сообщения, если в вашей учётной записи есть переписка с преподавателем.',
        'Технические данные:',
        '• сведения о входах: дата, IP-адрес, тип устройства, операционная система и браузер;',
        '• служебный идентификатор установки приложения — случайный номер, который приложение создаёт само. Он не связан с рекламными идентификаторами и не позволяет узнать ваше устройство где-то ещё;',
        '• журналы сервера: дата и время запроса, IP-адрес, адрес страницы, тип браузера или приложения и номер его версии;',
        '• сведения о сбое записи голоса: длительность записи и тип устройства — чтобы найти и исправить ошибку;',
        '• отчёты о сбоях приложения: текст ошибки, экран, версия приложения и системы — без имени, учётной записи и номера устройства, чтобы найти и исправить ошибку.',
        'Чего мы не собираем: дату рождения, адрес проживания, геолокацию устройства, контакты из телефона, рекламные идентификаторы (IDFA, Advertising ID), банковские данные.',
      ],
    },
    {
      title: '2. Зачем нам эти данные',
      body: [
        '• чтобы вы могли войти и учиться: показывать уроки, сохранять ответы и прогресс;',
        '• чтобы проверять задания — автоматически или силами преподавателя;',
        '• чтобы озвучивать и объяснять слова в вашем словаре;',
        '• чтобы отправлять письма, без которых не обойтись: код входа, пароль, ссылку для смены пароля;',
        '• чтобы защищать учётную запись — например, остановить подбор пароля;',
        '• чтобы находить и исправлять ошибки.',
        'Мы не показываем рекламу, не продаём данные и не передаём их рекламным сетям и брокерам данных. Мы не отслеживаем вас в других приложениях и на других сайтах.',
      ],
    },
    {
      title: '3. На каком основании',
      body: [
        '• Условия использования — без учётных и учебных данных сервис работать не может;',
        '• ваше согласие — на передачу данных за пределы Казахстана, описанную в разделе 5, и на уведомления;',
        '• закон — когда закон обязывает нас хранить или раскрыть сведения;',
        '• безопасность — технические данные и журналы входов.',
        'Согласие можно отозвать: написать нам или удалить учётную запись. Отзыв не касается данных, без которых сервис не может работать.',
      ],
    },
    {
      title: '4. Где хранятся данные',
      body: [
        'База данных, файлы и записи голоса хранятся на серверах в Республике Казахстан (Астана). Резервные копии — на втором сервере, тоже в Казахстане.',
        'Данные видят только те сотрудники, которым они нужны для работы: преподаватель — данные своих учеников, администрация — в объёме, нужном для работы сервиса.',
      ],
    },
    {
      title: '5. Кому передаются данные',
      body: [
        'Часть задач выполняют сторонние сервисы. Они получают только то, что нужно для их задачи, и работают по нашему поручению. Некоторые из них находятся за пределами Казахстана.',
        '• ElevenLabs (США) — только текст слова, чтобы озвучить слово в словаре. Ваш голос, имя и почта туда не передаются.',
        '• OpenAI (США) — текст слова, чтобы объяснить значение и перевод слова в словаре.',
        '• OpenAI (США) — текст письменного ответа или расшифровка устного ответа: автоматическая проверка в заданиях с такой проверкой, если они есть в вашей учётной записи.',
        '• OpenAI (США) — ваш голос в реальном времени и IP-адрес устройства: устная практика с автоматическим собеседником, если такое задание есть в вашей учётной записи. Голос идёт с устройства прямо в OpenAI и у нас не сохраняется, сохраняется только текст и оценка.',
        '• dictionaryapi.dev (открытый англоязычный словарь) — текст слова, чтобы найти словарную статью.',
        '• Resend (США) — адрес электронной почты и текст письма, чтобы доставить письмо: код входа, пароль, ссылку для смены пароля.',
        '• Apple — служебный токен устройства и текст уведомления, чтобы доставить push-уведомление, если вы его разрешили.',
        '• Google (YouTube), Vimeo — IP-адрес и сведения об устройстве: ролик урока загружается с их серверов, и они обрабатывают эти данные по своим правилам.',
        'В запросах к OpenAI мы не передаём ваше имя и адрес почты. Для защиты от злоупотреблений в устной практике передаётся зашифрованный номер учётной записи, из которого нельзя узнать, кто вы.',
        'Мы можем раскрыть данные, если этого требует закон, — по запросу суда или государственного органа.',
      ],
    },
    {
      title: '6. Сколько мы храним данные',
      body: [
        '• Учётная запись, прогресс, ответы, записи голоса, файлы, словарь — пока вы пользуетесь сервисом, а после удаления учётной записи — до вашего запроса на удаление данных (раздел 7).',
        '• Сведения о входах и устройствах — так же.',
        '• Журналы сервера — как правило, не дольше 30 дней.',
        '• Отчёты о сбоях приложения — 30 дней.',
        '• Резервные копии — до 30 дней.',
        '• Коды входа и ссылки для смены пароля — действуют от 15 минут до часа.',
      ],
    },
    {
      title: '7. Удаление учётной записи',
      body: [
        'В приложении: «Профиль» → «Настройки» → «Удалить профиль», затем подтверждение. Сразу после этого войти в учётную запись нельзя ни паролем, ни кодом из письма. Сеансы на всех устройствах завершаются: на этом — сразу, на остальных — не позже чем через минуту.',
        `Сами данные при этом остаются у нас: удаление закрывает вход, но не стирает прогресс, ответы, записи голоса, файлы и словарь. Если вы хотите, чтобы мы их удалили, напишите на ${CONTACT} с адреса, на который зарегистрирована учётная запись, — мы удалим их и ответим письмом. Останутся только сведения, которые мы обязаны хранить по закону, и обезличенные счётчики, по которым нельзя узнать вас.`,
        'Пока данные не удалены, учётную запись можно вернуть вместе с прогрессом и курсами — напишите нам. Зарегистрировать новую учётную запись на адрес удалённой нельзя: регистрация откажет. Напишите нам, и мы вернём прежнюю.',
        `Подробно — на странице ${DELETE_LINK}.`,
      ],
    },
    {
      title: '8. Ваши права',
      body: [
        '• узнать, какие данные о вас хранятся, и получить их копию;',
        '• исправить неточные данные — имя можно изменить в профиле, остальное по письму;',
        '• отозвать согласие;',
        '• удалить учётную запись и попросить нас удалить данные;',
        '• обратиться с жалобой в уполномоченный орган по защите персональных данных Республики Казахстан.',
        `Пишите на ${CONTACT}. Мы ответим в срок, установленный законом Республики Казахстан.`,
      ],
    },
    {
      title: '9. Как мы защищаем данные',
      body: [
        '• Соединение с сервисом всегда зашифровано (HTTPS).',
        '• Пароль, который вы задали сами, хранится только в виде необратимого хеша: узнать его не можем даже мы.',
        '• Если свой пароль вы не задавали, мы выдаём пароль из шести цифр и присылаем его письмом. Такой пароль хранится в зашифрованном виде, чтобы помочь вам войти, если письмо потерялось. Когда вы задаёте свой пароль в настройках, выданный перестаёт действовать, а его копия стирается.',
        '• В приложении данные для входа лежат в защищённом хранилище устройства (Keychain на iOS, Keystore на Android) и не попадают в резервные копии телефона.',
        '• Если произойдёт утечка, затрагивающая ваши данные, мы сообщим вам и уполномоченному органу в порядке, установленном законом.',
      ],
    },
    {
      title: '10. Возраст',
      body: [
        'Пользоваться сервисом самостоятельно можно с 16 лет. Если вам меньше 16, учётную запись заводит родитель или законный представитель, либо это происходит с его согласия, и он принимает эту политику.',
        'Мы осознанно не собираем данные детей младше 13 лет без согласия родителя. Если вы родитель и считаете, что ваш ребёнок зарегистрировался без вашего согласия, напишите нам — мы сотрём его данные.',
      ],
    },
    {
      title: '11. Cookie и данные на устройстве',
      body: [
        'Рекламных и аналитических cookie, счётчиков и трекеров в сервисе нет. На устройстве хранится только то, что нужно для работы: признак входа, выбранный язык, настройки, черновики неотправленных ответов.',
      ],
    },
    {
      title: '12. Изменения политики',
      body: [
        'Мы можем менять политику. Действующая редакция всегда по этой ссылке, дата — вверху. О существенных изменениях мы сообщим в приложении или письмом до того, как они начнут действовать.',
      ],
    },
    {
      title: '13. Контакты',
      body: [`${REQUISITES.ru}.`, `Электронная почта: ${CONTACT}.`],
    },
  ],
};

const privacyKk: LegalDoc = {
  title: 'Құпиялылық саясаты',
  updated: `${UPDATED} редакциясы`,
  intro: `Бұл саясат ${SERVICE} сервисі сіз туралы қандай деректерді жинайтынын, не үшін, олардың қайда және қанша уақыт сақталатынын, кімге берілетінін түсіндіреді. Сервис — ${APP_NAME} қолданбасы және academy.onebilim.kz веб-нұсқасы: олардың тіркелгісі біреу. Деректер операторы — ${REQUISITES.kk}. Бізбен байланыс: ${CONTACT}. Қолданба мен оның материалдары үшін біз ештеңе алмаймыз, қолданбада ештеңе сатылмайды, сондықтан біз ешқандай банк деректерін алмаймыз және сақтамаймыз.`,
  sections: [
    {
      title: '1. Қандай деректер жинаймыз',
      body: [
        'Өзіңіз көрсететін деректер:',
        '• аты-жөніңіз бен электрондық пошта мекенжайыңыз — тіркелу кезінде;',
        '• телефон нөмірі — қаласаңыз;',
        '• профиль суреті — егер жүктесеңіз;',
        '• интерфейс тілі және уақыт белдеуі.',
        'Оқу барысында пайда болатын деректер:',
        '• прогресс: қай сабақтар өтілді, ұпайлар, тапсырма нәтижелері;',
        '• тапсырмаларға берген жауаптарыңыз, соның ішінде мәтіндер;',
        '• дауыс жазбалары — ауызша жауап беру керек тапсырмаларда;',
        '• тапсырмаларға тіркейтін фотосуреттер мен файлдар;',
        '• сөздікке қосқан сөздеріңіз және оларды қайталау тарихы;',
        '• хабарламалар — егер тіркелгіңізде оқытушымен хат алмасу болса.',
        'Техникалық деректер:',
        '• кірулер туралы мәліметтер: күні, IP-мекенжай, құрылғы түрі, операциялық жүйе және браузер;',
        '• қолданбаны орнатудың қызметтік идентификаторы — қолданба өзі жасайтын кездейсоқ нөмір. Ол жарнамалық идентификаторлармен байланысты емес және құрылғыңызды басқа жерде тануға мүмкіндік бермейді;',
        '• сервер журналдары: сұраныстың күні мен уақыты, IP-мекенжай, бет мекенжайы, браузердің немесе қолданбаның түрі және оның нұсқа нөмірі;',
        '• дауыс жазу ақауы туралы мәліметтер: жазбаның ұзақтығы және құрылғы түрі — қатені тауып, түзету үшін;',
        '• қолданба ақаулары туралы есептер: қате мәтіні, экран, қолданба мен жүйенің нұсқасы — атыңызсыз, есептік жазбасыз және құрылғы нөмірінсіз, қатені тауып, түзету үшін.',
        'Біз жинамайтын деректер: туған күн, тұрғылықты мекенжай, құрылғының геолокациясы, телефондағы контактілер, жарнамалық идентификаторлар (IDFA, Advertising ID), банк деректері.',
      ],
    },
    {
      title: '2. Бұл деректер не үшін қажет',
      body: [
        '• сіз кіріп, оқи алуыңыз үшін: сабақтарды көрсету, жауаптар мен прогресті сақтау;',
        '• тапсырмаларды тексеру үшін — автоматты түрде немесе оқытушы арқылы;',
        '• сөздігіңіздегі сөздерді дыбыстау және түсіндіру үшін;',
        '• қажетті хаттарды жіберу үшін: кіру коды, құпиясөз, құпиясөзді ауыстыру сілтемесі;',
        '• тіркелгіні қорғау үшін — мысалы, құпиясөзді іріктеп табуды тоқтату;',
        '• қателерді тауып, түзету үшін.',
        'Біз жарнама көрсетпейміз, деректерді сатпаймыз және оларды жарнама желілері мен деректер брокерлеріне бермейміз. Біз сізді басқа қолданбалар мен сайттарда бақыламаймыз.',
      ],
    },
    {
      title: '3. Қандай негізде',
      body: [
        '• Пайдалану шарттары — тіркелгі және оқу деректерінсіз сервис жұмыс істей алмайды;',
        '• сіздің келісіміңіз — 5-бөлімде сипатталған деректерді Қазақстаннан тыс жерге беруге және хабарландыруларға;',
        '• заң — заң бізді мәліметтерді сақтауға немесе ашуға міндеттеген кезде;',
        '• қауіпсіздік — техникалық деректер және кірулер журналы.',
        'Келісімді кері қайтарып алуға болады: бізге жазыңыз немесе тіркелгіні жойыңыз. Бұл сервис онсыз жұмыс істей алмайтын деректерге қатысты емес.',
      ],
    },
    {
      title: '4. Деректер қайда сақталады',
      body: [
        'Дерекқор, файлдар мен дауыс жазбалары Қазақстан Республикасындағы (Астана) серверлерде сақталады. Сақтық көшірмелер — екінші серверде, ол да Қазақстанда.',
        'Деректерді тек жұмысы үшін қажет қызметкерлер ғана көреді: оқытушы — өз оқушыларының деректерін, әкімшілік — сервистің жұмысына қажетті көлемде.',
      ],
    },
    {
      title: '5. Деректер кімге беріледі',
      body: [
        'Кейбір міндеттерді бөгде сервистер орындайды. Олар өз міндетіне қажеттісін ғана алады және біздің тапсырмамыз бойынша жұмыс істейді. Олардың кейбірі Қазақстаннан тыс жерде орналасқан.',
        '• ElevenLabs (АҚШ) — тек сөздің мәтіні, сөздіктегі сөзді дыбыстау үшін. Дауысыңыз, аты-жөніңіз бен поштаңыз оған берілмейді.',
        '• OpenAI (АҚШ) — сөздің мәтіні, сөздіктегі сөздің мағынасы мен аудармасын түсіндіру үшін.',
        '• OpenAI (АҚШ) — жазбаша жауаптың мәтіні немесе ауызша жауаптың транскрипциясы: мұндай тексерісі бар тапсырмаларда автоматты тексеру, егер олар тіркелгіңізде болса.',
        '• OpenAI (АҚШ) — нақты уақыттағы дауысыңыз және құрылғының IP-мекенжайы: автоматты әңгімелесушімен ауызша жаттығу, егер мұндай тапсырма тіркелгіңізде болса. Дауыс құрылғыдан тікелей OpenAI-ға барады және бізде сақталмайды, тек мәтін мен баға сақталады.',
        '• dictionaryapi.dev (ашық ағылшын тілді сөздік) — сөздің мәтіні, сөздік мақаласын табу үшін.',
        '• Resend (АҚШ) — электрондық пошта мекенжайы және хаттың мәтіні, хатты жеткізу үшін: кіру коды, құпиясөз, құпиясөзді ауыстыру сілтемесі.',
        '• Apple — құрылғының қызметтік токені және хабарландыру мәтіні, егер рұқсат берсеңіз, push-хабарландыруды жеткізу үшін.',
        '• Google (YouTube), Vimeo — IP-мекенжай және құрылғы туралы мәліметтер: сабақ бейнесі олардың серверлерінен жүктеледі, және олар бұл деректерді өз ережелері бойынша өңдейді.',
        'OpenAI-ға сұраныстарда аты-жөніңіз бен пошта мекенжайыңызды бермейміз. Ауызша жаттығудағы теріс пайдаланудан қорғау үшін тіркелгінің шифрланған нөмірі беріледі, одан сіздің кім екеніңізді білу мүмкін емес.',
        'Заң талап етсе — сот немесе мемлекеттік органның сұрауы бойынша — деректерді ашуымыз мүмкін.',
      ],
    },
    {
      title: '6. Деректерді қанша уақыт сақтаймыз',
      body: [
        '• Тіркелгі, прогресс, жауаптар, дауыс жазбалары, файлдар, сөздік — сервисті пайдаланып жүргенде, ал тіркелгі жойылғаннан кейін — деректерді жою туралы сұрауыңызға дейін (7-бөлім).',
        '• Кірулер мен құрылғылар туралы мәліметтер — дәл солай.',
        '• Сервер журналдары — әдетте 30 күннен аспайды.',
        '• Қолданба ақаулары туралы есептер — 30 күн.',
        '• Сақтық көшірмелер — 30 күнге дейін.',
        '• Кіру кодтары мен құпиясөзді ауыстыру сілтемелері — 15 минуттан бір сағатқа дейін жарамды.',
      ],
    },
    {
      title: '7. Тіркелгіні жою',
      body: [
        'Қолданбада: «Профиль» → «Параметрлер» → «Профильді жою», содан кейін растау. Осыдан кейін бірден тіркелгіге құпиясөзбен де, хаттағы кодпен де кіру мүмкін емес. Барлық құрылғылардағы сеанстар аяқталады: осы құрылғыда — бірден, қалғандарында — бір минуттан кешіктірмей.',
        `Деректердің өзі бізде қалады: жою кіруді жабады, бірақ прогресті, жауаптарды, дауыс жазбаларын, файлдар мен сөздікті өшірмейді. Оларды өшіруімізді қаласаңыз, тіркелгі тіркелген мекенжайдан ${CONTACT} адресіне жазыңыз — өшіріп, хатпен жауап береміз. Тек заң бойынша сақтауға міндетті мәліметтер және жеке басыңызды анықтауға келмейтін жалпы статистика қалады.`,
        'Деректер өшірілмейінше, тіркелгіні прогресімен және курстарымен бірге қайтаруға болады — бізге жазыңыз. Жойылған тіркелгінің мекенжайына жаңа тіркелгі ашу мүмкін емес: тіркелу бас тартады. Бізге жазыңыз, біз бұрынғысын қайтарамыз.',
        `Толығырақ — ${DELETE_LINK} бетінде.`,
      ],
    },
    {
      title: '8. Сіздің құқықтарыңыз',
      body: [
        '• сіз туралы қандай деректер сақталатынын білу және олардың көшірмесін алу;',
        '• дәл емес деректерді түзету — атыңызды профильде өзгертуге болады, қалғанын хат арқылы;',
        '• келісімді кері қайтарып алу;',
        '• тіркелгіні жою және деректерді жоюды сұрау;',
        '• Қазақстан Республикасының дербес деректерді қорғау жөніндегі уәкілетті органына шағыммен жүгіну.',
        `${CONTACT} адресіне жазыңыз. Қазақстан Республикасының заңында белгіленген мерзімде жауап береміз.`,
      ],
    },
    {
      title: '9. Деректерді қалай қорғаймыз',
      body: [
        '• Сервиспен байланыс әрқашан шифрланған (HTTPS).',
        '• Өзіңіз қойған құпиясөз тек қайтымсыз хеш түрінде сақталады: оны тіпті біз де біле алмаймыз.',
        '• Өз құпиясөзіңізді қоймаған болсаңыз, біз алты цифрдан тұратын құпиясөз беріп, оны хатпен жібереміз. Хат жоғалса, кіруге көмектесу үшін мұндай құпиясөз шифрланған түрде сақталады. Параметрлерде өз құпиясөзіңізді қойғанда, берілген құпиясөз жарамсыз болады, ал оның көшірмесі өшіріледі.',
        '• Қолданбада кіру деректері құрылғының қорғалған қоймасында (iOS-та Keychain, Android-та Keystore) сақталады және телефонның сақтық көшірмелеріне түспейді.',
        '• Деректеріңізге қатысты ақпарат жылыстаса, сізге және уәкілетті органға заңда белгіленген тәртіппен хабарлаймыз.',
      ],
    },
    {
      title: '10. Жас',
      body: [
        'Сервисті 16 жастан бастап өз бетіңізше пайдалануға болады. 16-ға толмаған болсаңыз, тіркелгіні ата-анаңыз немесе заңды өкіліңіз ашады не бұл оның келісімімен жасалады, және ол осы саясатты қабылдайды.',
        'Біз 13 жасқа толмаған балалардың деректерін ата-анасының келісімінсіз әдейі жинамаймыз. Ата-ана ретінде балаңыз сіздің келісіміңізсіз тіркелді деп есептесеңіз, бізге жазыңыз — оның деректерін өшіреміз.',
      ],
    },
    {
      title: '11. Cookie және құрылғыдағы деректер',
      body: [
        'Сервисте жарнамалық және аналитикалық cookie, есептегіштер мен трекерлер жоқ. Құрылғыда тек жұмысқа қажеттісі сақталады: кіру белгісі, таңдалған тіл, параметрлер, жіберілмеген жауаптардың жобалары.',
      ],
    },
    {
      title: '12. Саясаттың өзгеруі',
      body: [
        'Біз саясатты өзгерте аламыз. Қолданыстағы редакция әрқашан осы сілтеме бойынша, күні — жоғарыда. Елеулі өзгерістер туралы олар күшіне енгенге дейін қолданбада немесе хатпен хабарлаймыз.',
      ],
    },
    {
      title: '13. Байланыс',
      body: [`${REQUISITES.kk}.`, `Электрондық пошта: ${CONTACT}.`],
    },
  ],
};

const privacyEn: LegalDoc = {
  title: 'Privacy Policy',
  updated: `Version of ${UPDATED}`,
  intro: `This policy explains what data the ${SERVICE} service collects about you, why, where it is stored, for how long and who it is shared with. The service is the ${APP_NAME} app and the web version at academy.onebilim.kz: both use the same account. The data controller is ${REQUISITES.en}. Write to us at ${CONTACT}. We do not charge for the app or any of its content, nothing is sold in the app, and so we never receive or store any bank card details.`,
  sections: [
    {
      title: '1. What data we collect',
      body: [
        'What you give us:',
        '• your name and email address, when you sign up;',
        '• your phone number, if you choose to add it;',
        '• a profile picture, if you upload one;',
        '• your interface language and time zone.',
        'What appears while you study:',
        '• your progress: completed lessons, points, task results;',
        '• your answers to tasks, including written ones;',
        '• voice recordings, in tasks that ask for a spoken answer;',
        '• photos and files you attach to tasks;',
        '• words you add to your vocabulary and your review history;',
        '• messages, if your account includes conversations with a teacher.',
        'Technical data:',
        '• sign-in details: date, IP address, device type, operating system and browser;',
        '• an installation ID: a random number the app creates by itself. It is not an advertising identifier and cannot be used to recognise your device anywhere else;',
        '• server logs: date and time of the request, IP address, page address, browser or app type and its version number;',
        '• voice recording failure reports: recording length and device type, so that we can find and fix the problem;',
        '• app crash reports: error text, screen, app and system version — without your name, account or device number, so that we can find and fix the problem.',
        'What we do not collect: date of birth, home address, device location, phone contacts, advertising identifiers (IDFA, Advertising ID), bank card details.',
      ],
    },
    {
      title: '2. Why we need this data',
      body: [
        '• to let you sign in and study: show lessons, save your answers and progress;',
        '• to check tasks, automatically or by a teacher;',
        '• to pronounce and explain the words in your vocabulary;',
        '• to send essential emails: sign-in code, password, password reset link;',
        '• to protect your account, for example by stopping password guessing;',
        '• to find and fix errors.',
        'We do not show ads, we do not sell data and we do not share it with ad networks or data brokers. We do not track you across other apps or websites.',
      ],
    },
    {
      title: '3. Legal grounds',
      body: [
        '• the Terms of Use: the service cannot work without account and study data;',
        '• your consent: for the transfers outside Kazakhstan described in section 5, and for notifications;',
        '• the law: where the law requires us to keep or disclose information;',
        '• security: technical data and sign-in records.',
        'You can withdraw your consent by writing to us or by deleting your account. This does not affect data the service cannot work without.',
      ],
    },
    {
      title: '4. Where data is stored',
      body: [
        'The database, files and voice recordings are stored on servers in the Republic of Kazakhstan (Astana). Backups are kept on a second server, also in Kazakhstan.',
        'Only staff who need the data for their work can see it: a teacher sees the data of their own students, the administration sees what it needs to run the service.',
      ],
    },
    {
      title: '5. Who we share data with',
      body: [
        'Some tasks are handled by third-party services. They receive only what they need for their task and act on our behalf. Some of them are located outside Kazakhstan.',
        '• ElevenLabs (USA): only the text of a word, to pronounce a word in your vocabulary. Your voice, name and email are not sent.',
        '• OpenAI (USA): the text of a word, to explain the meaning and translation of a word in your vocabulary.',
        '• OpenAI (USA): the text of a written answer or a transcript of a spoken answer, for automatic checking in tasks that have it, if your account includes them.',
        '• OpenAI (USA): your voice in real time and your device\'s IP address, for spoken practice with an automatic conversation partner, if your account includes such a task. Your voice goes from your device straight to OpenAI and is not stored by us; only the text and the score are saved.',
        '• dictionaryapi.dev (open English dictionary): the text of a word, to find a dictionary entry.',
        '• Resend (USA): your email address and the text of the email, to deliver emails: sign-in code, password, password reset link.',
        '• Apple: a device token and the notification text, to deliver push notifications, if you allowed them.',
        '• Google (YouTube), Vimeo: IP address and device details. The lesson video loads from their servers, and they process this data under their own rules.',
        'We do not send your name or email address to OpenAI. To prevent abuse in spoken practice, an encrypted account number is sent that cannot be used to find out who you are.',
        'We may disclose data where the law requires it, at the request of a court or a public authority.',
      ],
    },
    {
      title: '6. How long we keep data',
      body: [
        '• Account, progress, answers, voice recordings, files, vocabulary: while you use the service, and after the account is deleted until you ask us to delete the data (section 7).',
        '• Sign-in and device records: the same.',
        '• Server logs: usually no longer than 30 days.',
        '• App crash reports: 30 days.',
        '• Backups: up to 30 days.',
        '• Sign-in codes and password reset links: valid for 15 minutes to one hour.',
      ],
    },
    {
      title: '7. Deleting your account',
      body: [
        'In the app: Profile → Settings → "Delete account", then confirm. From that moment you cannot sign in to the account, either with the password or with a code from an email. Sessions on all devices end: on this one immediately, on the others within a minute.',
        `The data itself stays with us: deletion closes access to the account, but it does not erase your progress, answers, voice recordings, files or vocabulary. If you want us to delete them, write to ${CONTACT} from the email address the account is registered to — we will delete them and confirm by email. Only information we are required by law to keep, and anonymous counts that cannot identify you, will remain.`,
        'Until the data is deleted, the account can be restored together with your progress and courses: just write to us. You cannot sign up for a new account with the email of a deleted one: sign-up will be refused. Write to us and we will restore the old one.',
        `Details: ${DELETE_LINK}.`,
      ],
    },
    {
      title: '8. Your rights',
      body: [
        '• find out what data we hold about you and get a copy of it;',
        '• correct inaccurate data: you can change your name in your profile, anything else by email;',
        '• withdraw your consent;',
        '• delete your account and ask us to delete your data;',
        '• complain to the authorised personal data protection body of the Republic of Kazakhstan.',
        `Write to ${CONTACT}. We will reply within the time set by the law of the Republic of Kazakhstan.`,
      ],
    },
    {
      title: '9. How we protect data',
      body: [
        '• The connection to the service is always encrypted (HTTPS).',
        '• A password you set yourself is stored only as a one-way hash: even we cannot find it out.',
        '• If you have not set your own password, we issue a six-digit password and send it by email. That password is stored encrypted so that we can help you sign in if the email gets lost. When you set your own password in settings, the issued one stops working and its copy is erased.',
        '• In the app, sign-in data is kept in the device\'s secure storage (Keychain on iOS, Keystore on Android) and is not included in phone backups.',
        '• If a data breach affects your data, we will inform you and the authorised body as the law requires.',
      ],
    },
    {
      title: '10. Age',
      body: [
        'You can use the service on your own from the age of 16. If you are under 16, the account is created by a parent or legal guardian, or with their consent, and they accept this policy.',
        'We do not knowingly collect data from children under 13 without a parent\'s consent. If you are a parent and believe your child signed up without your consent, write to us and we will erase the child\'s data.',
      ],
    },
    {
      title: '11. Cookies and data on your device',
      body: [
        'The service has no advertising or analytics cookies, counters or trackers. Your device stores only what the service needs to work: your sign-in state, chosen language, settings and drafts of unsent answers.',
      ],
    },
    {
      title: '12. Changes to this policy',
      body: [
        'We may change this policy. The current version is always at this link, with the date at the top. We will tell you about significant changes in the app or by email before they take effect.',
      ],
    },
    {
      title: '13. Contacts',
      body: [`${REQUISITES.en}.`, `Email: ${CONTACT}.`],
    },
  ],
};

/* ─────────────────── условия использования ─────────────────── */

const termsRu: LegalDoc = {
  title: 'Условия использования',
  updated: `Редакция от ${UPDATED}`,
  intro: `Эти условия действуют для сервиса ${SERVICE} — приложения ${APP_NAME} и веб-версии academy.onebilim.kz. Сервис предоставляет ${REQUISITES.ru}. Регистрируясь или пользуясь сервисом, вы принимаете эти условия и Политику конфиденциальности (${PRIVACY_LINK}).`,
  sections: [
    {
      title: '1. Что это за сервис',
      body: [
        `${SERVICE} — сервис для изучения английского языка: видеоуроки, упражнения с проверкой, словарь с озвучкой и повторением слов.`,
        'Мы ничего не взимаем за приложение и его материалы. В приложении ничего не продаётся.',
        'Состав курсов и уроков может меняться: мы добавляем новые материалы, обновляем и убираем старые.',
      ],
    },
    {
      title: '2. Учётная запись',
      body: [
        '• Учётная запись — на одного человека. Прогресс и результаты относятся только к вам, поэтому передавать вход другим нельзя.',
        '• Указывайте настоящее имя и адрес почты, к которому у вас есть доступ: на него приходят код входа и пароль.',
        '• Берегите пароль. Если вы заметили, что учётной записью пользуется кто-то другой, напишите нам.',
        '• Удалить учётную запись можно в приложении: «Профиль» → «Настройки» → «Удалить профиль». Что происходит с данными после удаления — в Политике конфиденциальности, раздел 7.',
      ],
    },
    {
      title: '3. Возраст',
      body: [
        'Пользоваться сервисом самостоятельно можно с 16 лет. Если вам меньше 16, учётную запись заводит родитель или законный представитель, либо это происходит с его согласия, и он принимает эти условия.',
      ],
    },
    {
      title: '4. Что делать нельзя',
      body: [
        '• копировать, записывать с экрана, публиковать и распространять материалы сервиса — тексты, аудио, видео и задания;',
        '• передавать вход в учётную запись другим людям;',
        '• пытаться получить чужие данные, мешать работе сервиса, обходить его ограничения, заходить в него программами-роботами;',
        '• оскорблять других учеников, преподавателей и сотрудников;',
        '• загружать чужие материалы без разрешения их автора, а также незаконные или оскорбительные материалы.',
        'Если эти правила нарушены, мы можем ограничить или прекратить работу учётной записи. Обычно мы сначала пишем вам.',
      ],
    },
    {
      title: '5. Материалы сервиса',
      body: [
        'Уроки, задания, тексты, аудио, видео и программы сервиса принадлежат оператору сервиса, указанному выше, или используются им законно. Ими можно пользоваться для своей учёбы в сервисе, но не для других целей.',
      ],
    },
    {
      title: '6. Ваши работы',
      body: [
        'Ответы, тексты, записи голоса и файлы, которые вы создаёте, остаются вашими. Вы разрешаете нам хранить и обрабатывать их, чтобы показывать ваш прогресс и проверять задания, — в том числе с помощью сервисов, перечисленных в Политике конфиденциальности.',
      ],
    },
    {
      title: '7. Автоматическая проверка',
      body: [
        'Некоторые задания проверяются автоматически. Такая оценка помогает учиться, но не является официальным результатом экзамена и может ошибаться. Чтобы сервис работал ровно для всех, у автоматических функций есть дневные ограничения.',
      ],
    },
    {
      title: '8. Работа сервиса',
      body: [
        'Мы стараемся, чтобы сервис работал без перерывов, но не можем этого гарантировать: бывают технические работы и сбои связи. О плановых работах мы предупреждаем заранее, когда это возможно.',
        'Сервис предоставляется таким, какой он есть. Мы не отвечаем за косвенные потери из-за перерывов в работе, в той мере, в какой это допускает закон.',
      ],
    },
    {
      title: '9. Магазины приложений',
      body: [
        'Если вы установили приложение из App Store или Google Play, действуют также правила этих магазинов. За приложение и его поддержку отвечаем мы, а не Apple и не Google.',
      ],
    },
    {
      title: '10. Закон и споры',
      body: [
        'К этим условиям применяется право Республики Казахстан. Споры мы стараемся решить перепиской; если не получилось — в суде по месту нахождения оператора сервиса, если закон не требует иного.',
      ],
    },
    {
      title: '11. Изменения условий',
      body: [
        'Мы можем менять эти условия. Действующая редакция всегда по этой ссылке, дата — вверху. О существенных изменениях мы сообщим в приложении или письмом заранее. Если вы не согласны с новой редакцией, вы можете удалить учётную запись.',
      ],
    },
    {
      title: '12. Контакты',
      body: [`${REQUISITES.ru}.`, `Электронная почта: ${CONTACT}.`],
    },
  ],
};

const termsKk: LegalDoc = {
  title: 'Пайдалану шарттары',
  updated: `${UPDATED} редакциясы`,
  intro: `Бұл шарттар ${SERVICE} сервисіне — ${APP_NAME} қолданбасына және academy.onebilim.kz веб-нұсқасына қолданылады. Сервисті ${REQUISITES.kk} ұсынады. Тіркелу немесе сервисті пайдалану арқылы сіз осы шарттарды және Құпиялылық саясатын (${PRIVACY_LINK}) қабылдайсыз.`,
  sections: [
    {
      title: '1. Бұл қандай сервис',
      body: [
        `${SERVICE} — ағылшын тілін үйренуге арналған сервис: бейнесабақтар, тексерісі бар жаттығулар, сөздерді дыбыстайтын және қайталайтын сөздік.`,
        'Қолданба мен оның материалдары үшін біз ештеңе алмаймыз. Қолданбада ештеңе сатылмайды.',
        'Курстар мен сабақтардың құрамы өзгеруі мүмкін: біз жаңа материалдар қосамыз, ескілерін жаңартамыз және алып тастаймыз.',
      ],
    },
    {
      title: '2. Тіркелгі',
      body: [
        '• Тіркелгі — бір адамға арналған. Прогресс пен нәтижелер тек сізге тиесілі, сондықтан кіруді басқаларға беруге болмайды.',
        '• Шын атыңызды және өзіңіз оқи алатын пошта мекенжайын көрсетіңіз: оған кіру коды мен құпиясөз келеді.',
        '• Құпиясөзіңізді сақтаңыз. Тіркелгіңізді басқа біреу пайдаланып жүргенін байқасаңыз, бізге жазыңыз.',
        '• Тіркелгіні қолданбада жоюға болады: «Профиль» → «Параметрлер» → «Профильді жою». Жойылғаннан кейін деректермен не болатыны — Құпиялылық саясатының 7-бөлімінде.',
      ],
    },
    {
      title: '3. Жас',
      body: [
        'Сервисті 16 жастан бастап өз бетіңізше пайдалануға болады. 16-ға толмаған болсаңыз, тіркелгіні ата-анаңыз немесе заңды өкіліңіз ашады не бұл оның келісімімен жасалады, және ол осы шарттарды қабылдайды.',
      ],
    },
    {
      title: '4. Не істеуге болмайды',
      body: [
        '• сервис материалдарын — мәтіндерді, аудионы, бейнені және тапсырмаларды — көшіруге, экраннан жазып алуға, жариялауға және таратуға;',
        '• тіркелгіге кіруді басқа адамдарға беруге;',
        '• бөтен деректерді алуға тырысуға, сервистің жұмысына кедергі келтіруге, оның шектеулерін айналып өтуге, оған робот-бағдарламалармен кіруге;',
        '• басқа оқушыларды, оқытушыларды және қызметкерлерді қорлауға;',
        '• бөтен материалдарды авторының рұқсатынсыз, сондай-ақ заңсыз немесе қорлайтын материалдарды жүктеуге.',
        'Бұл ережелер бұзылса, тіркелгінің жұмысын шектеуіміз немесе тоқтатуымыз мүмкін. Әдетте алдымен сізге жазамыз.',
      ],
    },
    {
      title: '5. Сервис материалдары',
      body: [
        'Сервистің сабақтары, тапсырмалары, мәтіндері, аудиосы, бейнесі және бағдарламалары жоғарыда көрсетілген сервис операторына тиесілі немесе ол оларды заңды түрде пайдаланады. Оларды сервисте өз оқуыңыз үшін пайдалануға болады, бірақ басқа мақсаттарға емес.',
      ],
    },
    {
      title: '6. Сіздің жұмыстарыңыз',
      body: [
        'Сіз жасаған жауаптар, мәтіндер, дауыс жазбалары мен файлдар сіздікі болып қалады. Прогресіңізді көрсету және тапсырмаларды тексеру үшін — соның ішінде Құпиялылық саясатында аталған сервистердің көмегімен — оларды сақтауға және өңдеуге бізге рұқсат бересіз.',
      ],
    },
    {
      title: '7. Автоматты тексеру',
      body: [
        'Кейбір тапсырмалар автоматты түрде тексеріледі. Мұндай баға оқуға көмектеседі, бірақ ресми емтихан нәтижесі болып саналмайды және қателесуі мүмкін. Сервис бәріне бірдей жұмыс істеуі үшін автоматты функциялардың күндік шектеулері бар.',
      ],
    },
    {
      title: '8. Сервистің жұмысы',
      body: [
        'Сервистің үзіліссіз жұмыс істеуіне тырысамыз, бірақ оған кепілдік бере алмаймыз: техникалық жұмыстар мен байланыс ақаулары болады. Жоспарлы жұмыстар туралы мүмкіндігінше алдын ала ескертеміз.',
        'Сервис қандай болса, солай ұсынылады. Заң рұқсат ететін шамада жұмыстағы үзілістерден болған жанама шығындарға жауап бермейміз.',
      ],
    },
    {
      title: '9. Қолданбалар дүкендері',
      body: [
        'Қолданбаны App Store немесе Google Play дүкенінен орнатсаңыз, сол дүкендердің ережелері де қолданылады. Қолданба мен оны қолдауға Apple немесе Google емес, біз жауап береміз.',
      ],
    },
    {
      title: '10. Заң және даулар',
      body: [
        'Бұл шарттарға Қазақстан Республикасының құқығы қолданылады. Дауларды хат алмасу арқылы шешуге тырысамыз; болмаса — заң өзгеше талап етпесе, сервис операторы орналасқан жердегі сотта.',
      ],
    },
    {
      title: '11. Шарттардың өзгеруі',
      body: [
        'Біз бұл шарттарды өзгерте аламыз. Қолданыстағы редакция әрқашан осы сілтеме бойынша, күні — жоғарыда. Елеулі өзгерістер туралы қолданбада немесе хатпен алдын ала хабарлаймыз. Жаңа редакциямен келіспесеңіз, тіркелгіні жоя аласыз.',
      ],
    },
    {
      title: '12. Байланыс',
      body: [`${REQUISITES.kk}.`, `Электрондық пошта: ${CONTACT}.`],
    },
  ],
};

const termsEn: LegalDoc = {
  title: 'Terms of Use',
  updated: `Version of ${UPDATED}`,
  intro: `These terms apply to the ${SERVICE} service: the ${APP_NAME} app and the web version at academy.onebilim.kz. The service is provided by ${REQUISITES.en}. By signing up or using the service, you accept these terms and the Privacy Policy (${PRIVACY_LINK}).`,
  sections: [
    {
      title: '1. About the service',
      body: [
        `${SERVICE} is a service for learning English: video lessons, exercises with checking, and a vocabulary trainer with audio and word review.`,
        'We do not charge for the app or any of its content. Nothing is sold in the app.',
        'The courses and lessons may change: we add new materials and update or remove old ones.',
      ],
    },
    {
      title: '2. Your account',
      body: [
        '• One account is for one person. Progress and results belong to you only, so do not share your sign-in with others.',
        '• Use your real name and an email address you can read: your sign-in code and password are sent there.',
        '• Keep your password safe. If you notice someone else using your account, write to us.',
        '• You can delete your account in the app: Profile → Settings → "Delete account". What happens to your data after deletion is described in section 7 of the Privacy Policy.',
      ],
    },
    {
      title: '3. Age',
      body: [
        'You can use the service on your own from the age of 16. If you are under 16, the account is created by a parent or legal guardian, or with their consent, and they accept these terms.',
      ],
    },
    {
      title: '4. What you must not do',
      body: [
        '• copy, screen-record, publish or distribute the service\'s materials: texts, audio, video and tasks;',
        '• let other people sign in to your account;',
        '• try to get other people\'s data, disrupt the service, get around its limits, or use bots or scripts on it;',
        '• insult other students, teachers or staff;',
        '• upload other people\'s work without their permission, or any unlawful or offensive material.',
        'If these rules are broken, we may restrict or end the account. We usually write to you first.',
      ],
    },
    {
      title: '5. Service materials',
      body: [
        'Lessons, tasks, texts, audio, video and software of the service belong to the service operator named above or are used by it lawfully. You may use them for your own study in the service, but not for any other purpose.',
      ],
    },
    {
      title: '6. Your work',
      body: [
        'Answers, texts, voice recordings and files you create remain yours. You allow us to store and process them to show your progress and check your tasks, including with the help of the services listed in the Privacy Policy.',
      ],
    },
    {
      title: '7. Automatic checking',
      body: [
        'Some tasks are checked automatically. Such a score helps you learn, but it is not an official exam result and it can be wrong. To keep the service running smoothly for everyone, automatic features have daily limits.',
      ],
    },
    {
      title: '8. How the service works',
      body: [
        'We do our best to keep the service running without interruptions, but we cannot guarantee it: there may be maintenance and network problems. We announce planned maintenance in advance when we can.',
        'The service is provided as it is. To the extent the law allows, we are not liable for indirect losses caused by interruptions.',
      ],
    },
    {
      title: '9. App stores',
      body: [
        'If you installed the app from the App Store or Google Play, the rules of that store also apply. We, not Apple or Google, are responsible for the app and its support.',
      ],
    },
    {
      title: '10. Law and disputes',
      body: [
        'These terms are governed by the law of the Republic of Kazakhstan. We try to settle disputes by correspondence; if that does not work, disputes go to the court at the location of the service operator, unless the law requires otherwise.',
      ],
    },
    {
      title: '11. Changes to these terms',
      body: [
        'We may change these terms. The current version is always at this link, with the date at the top. We will tell you about significant changes in the app or by email in advance. If you do not agree with the new version, you can delete your account.',
      ],
    },
    {
      title: '12. Contacts',
      body: [`${REQUISITES.en}.`, `Email: ${CONTACT}.`],
    },
  ],
};

export const PRIVACY: Record<string, LegalDoc> = { ru: privacyRu, kk: privacyKk, en: privacyEn };
export const TERMS: Record<string, LegalDoc> = { ru: termsRu, kk: termsKk, en: termsEn };

/* ─────────────────── удаление аккаунта ───────────────────
 *
 * Отдельная ПУБЛИЧНАЯ страница — требование Google Play: в кабинете нужен
 * работающий адрес, где написано, как удалить аккаунт, что удаляется, а что
 * остаётся и почему. Ссылку проверяют открытием из чистого браузера, поэтому
 * страница живёт вне платформы и не требует входа.
 *
 * ⚠️ Каждое утверждение здесь сверено с кодом — список в шапке файла. Срока стирания
 * нет и не пишется (D71): задания, которое стирает данные, нет, стирание — ручное по письму.
 */

const deleteRu: LegalDoc = {
  title: 'Удаление аккаунта',
  updated: `Редакция от ${UPDATED}`,
  intro: `Здесь — как удалить учётную запись ${SERVICE}, что происходит сразу, что остаётся и как попросить удалить сами данные. Оператор данных — ${REQUISITES.ru}.`,
  sections: [
    {
      title: '1. Как удалить аккаунт',
      body: [
        `• В приложении ${APP_NAME}: «Профиль» → «Настройки» → «Удалить профиль», затем подтверждение.`,
        `• Если вы пользуетесь только веб-версией, напишите на ${CONTACT} с адреса, на который зарегистрирована учётная запись, — мы закроем её так же.`,
      ],
    },
    {
      title: '2. Что происходит сразу',
      body: [
        '• Войти в учётную запись больше нельзя — ни паролем, ни кодом из письма.',
        '• Сеансы на всех устройствах завершаются: на этом — сразу, на остальных — не позже чем через минуту.',
        '• Записи на курсы закрываются.',
      ],
    },
    {
      title: '3. Что остаётся',
      body: [
        'Удаление закрывает вход, но не стирает данные: имя, адрес почты, прогресс, ответы, записи голоса, файлы и словарь остаются у нас.',
        `Чтобы мы удалили и сами данные, напишите на ${CONTACT} с адреса, на который зарегистрирована учётная запись. Мы удалим их и ответим письмом. Останутся только сведения, которые мы обязаны хранить по закону, и обезличенные счётчики, по которым нельзя узнать вас.`,
      ],
    },
    {
      title: '4. Если передумали',
      body: [
        'Пока данные не удалены, учётную запись можно вернуть вместе с прогрессом и курсами — напишите нам.',
        'Зарегистрировать новую учётную запись на адрес удалённой нельзя: регистрация откажет. Напишите нам, и мы вернём прежнюю.',
      ],
    },
    {
      title: '5. Связаться с нами',
      body: [`Оператор: ${REQUISITES.ru}.`, `Электронная почта: ${CONTACT}.`],
    },
  ],
};

const deleteKk: LegalDoc = {
  title: 'Аккаунтты жою',
  updated: `${UPDATED} редакциясы`,
  intro: `Мұнда — ${SERVICE} тіркелгісін қалай жоюға болатыны, бірден не болатыны, не қалатыны және деректердің өзін жоюды қалай сұрауға болатыны. Деректер операторы — ${REQUISITES.kk}.`,
  sections: [
    {
      title: '1. Аккаунтты қалай жоюға болады',
      body: [
        `• ${APP_NAME} қолданбасында: «Профиль» → «Параметрлер» → «Профильді жою», содан кейін растау.`,
        `• Тек веб-нұсқаны пайдалансаңыз, тіркелгі тіркелген мекенжайдан ${CONTACT} адресіне жазыңыз — біз оны дәл солай жабамыз.`,
      ],
    },
    {
      title: '2. Бірден не болады',
      body: [
        '• Тіркелгіге енді кіру мүмкін емес — құпиясөзбен де, хаттағы кодпен де.',
        '• Барлық құрылғылардағы сеанстар аяқталады: осы құрылғыда — бірден, қалғандарында — бір минуттан кешіктірмей.',
        '• Курстарға жазылулар жабылады.',
      ],
    },
    {
      title: '3. Не қалады',
      body: [
        'Жою кіруді жабады, бірақ деректерді өшірмейді: аты-жөніңіз, пошта мекенжайыңыз, прогресс, жауаптар, дауыс жазбалары, файлдар мен сөздік бізде қалады.',
        `Деректердің өзін де өшіруімізді қаласаңыз, тіркелгі тіркелген мекенжайдан ${CONTACT} адресіне жазыңыз. Өшіріп, хатпен жауап береміз. Тек заң бойынша сақтауға міндетті мәліметтер және жеке басыңызды анықтауға келмейтін жалпы статистика қалады.`,
      ],
    },
    {
      title: '4. Ойыңыз өзгерсе',
      body: [
        'Деректер өшірілмейінше, тіркелгіні прогресімен және курстарымен бірге қайтаруға болады — бізге жазыңыз.',
        'Жойылған тіркелгінің мекенжайына жаңа тіркелгі ашу мүмкін емес: тіркелу бас тартады. Бізге жазыңыз, біз бұрынғысын қайтарамыз.',
      ],
    },
    {
      title: '5. Бізбен байланыс',
      body: [`Оператор: ${REQUISITES.kk}.`, `Электрондық пошта: ${CONTACT}.`],
    },
  ],
};

const deleteEn: LegalDoc = {
  title: 'Delete your account',
  updated: `Version of ${UPDATED}`,
  intro: `How to delete your ${SERVICE} account, what happens straight away, what stays, and how to ask us to delete the data itself. Data controller: ${REQUISITES.en}.`,
  sections: [
    {
      title: '1. How to delete the account',
      body: [
        `• In the ${APP_NAME} app: Profile → Settings → "Delete account", then confirm.`,
        `• If you only use the web version, write to ${CONTACT} from the email address the account is registered to, and we will close it in the same way.`,
      ],
    },
    {
      title: '2. What happens straight away',
      body: [
        '• You can no longer sign in to the account, either with the password or with a code from an email.',
        '• Sessions on all devices end: on this one immediately, on the others within a minute.',
        '• Your places in courses are closed.',
      ],
    },
    {
      title: '3. What stays',
      body: [
        'Deletion closes access to the account but does not erase the data: your name, email address, progress, answers, voice recordings, files and vocabulary stay with us.',
        `To have the data itself deleted, write to ${CONTACT} from the email address the account is registered to. We will delete it and confirm by email. Only information we are required by law to keep, and anonymous counts that cannot identify you, will remain.`,
      ],
    },
    {
      title: '4. If you change your mind',
      body: [
        'Until the data is deleted, the account can be restored together with your progress and courses: just write to us.',
        'You cannot sign up for a new account with the email of a deleted one: sign-up will be refused. Write to us and we will restore the old one.',
      ],
    },
    {
      title: '5. Contact',
      body: [`Operator: ${REQUISITES.en}.`, `Email: ${CONTACT}.`],
    },
  ],
};

/* ─────────────────── поддержка ───────────────────
 *
 * ⚠️ ОТДЕЛЬНАЯ СТРАНИЦА, А НЕ ГЛАВНАЯ САЙТА. До 31.08.2026 в кабинете Apple в
 * поле «ссылка поддержки» стоял адрес витрины academy.onebilim.kz. Проверяющий
 * открывает эту ссылку ВСЕГДА — это обязательный пункт его проверки, — и попал
 * на страницу продаж курсов. Приложенный к отказу снимок был именно оттуда.
 *
 * Поддержка — это «куда написать, если что-то не работает», а не витрина.
 */

const supportRu: LegalDoc = {
  title: 'Поддержка',
  updated: `Редакция от ${UPDATED}`,
  intro: `Здесь — как связаться с нами, если что-то не работает или есть вопрос по учёбе. Отвечаем в рабочие дни, обычно в течение суток.`,
  sections: [
    {
      title: '1. Как связаться',
      body: [
        `• Электронная почта: ${CONTACT} — пишите с того адреса, на который заведена учётная запись, так мы быстрее вас найдём.`,
      ],
    },
    {
      title: '2. С чем помогаем',
      body: [
        '• Не приходит письмо с кодом для входа. Проверьте папку «Спам»; если письма нет, напишите нам — вышлем код вручную.',
        '• Забыли пароль. На экране входа нажмите «Забыли пароль» — придёт ссылка для смены.',
        '• Не открывается урок или задание. Напишите, что именно и на каком экране, — по возможности приложите снимок экрана.',
        '• Не воспроизводится видео или аудио. Чаще всего помогает переустановка приложения или другая сеть; если нет — напишите нам.',
        '• Вопросы по учёбе: расписание, домашние работы, уровень, подготовка к экзамену.',
      ],
    },
    {
      title: '3. Учётная запись',
      body: [
        `• Удалить учётную запись можно самостоятельно в приложении ${APP_NAME}: «Профиль» → «Настройки» → «Удалить профиль». Из веб-версии — письмом на ${CONTACT}. Подробнее — на странице «Удаление аккаунта».`,
        '• Изменить имя, язык и уведомления — в разделе «Профиль».',
      ],
    },
    {
      title: '4. Документы',
      body: [
        '• Политика конфиденциальности — /privacy',
        '• Условия использования — /terms',
        '• Удаление аккаунта — /delete-account',
      ],
    },
    {
      title: '5. Кто мы',
      body: [REQUISITES.ru],
    },
  ],
};

const supportEn: LegalDoc = {
  title: 'Support',
  updated: `Revision of ${UPDATED}`,
  intro: `How to reach us if something does not work or you have a question about your studies. We reply on business days, usually within 24 hours.`,
  sections: [
    {
      title: '1. How to reach us',
      body: [
        `• Email: ${CONTACT} — please write from the address your account is registered with, so we can find you faster.`,
      ],
    },
    {
      title: '2. What we help with',
      body: [
        '• The sign-in code email does not arrive. Check the spam folder; if it is not there, write to us and we will send the code manually.',
        '• Forgotten password. On the sign-in screen tap "Forgot password" and we will email a reset link.',
        '• A lesson or task does not open. Tell us what exactly and on which screen, a screenshot helps.',
        '• Video or audio does not play. Reinstalling the app or switching networks usually helps; if not, write to us.',
        '• Study questions: schedule, homework, level, exam preparation.',
      ],
    },
    {
      title: '3. Your account',
      body: [
        `• You can delete your account yourself in the ${APP_NAME} app: Profile → Settings → "Delete account". From the web version, write to ${CONTACT}. See the "Delete your account" page for details.`,
        '• Name, language and notification settings are in the Profile section.',
      ],
    },
    {
      title: '4. Documents',
      body: [
        '• Privacy policy — /privacy',
        '• Terms of use — /terms',
        '• Account deletion — /delete-account',
      ],
    },
    {
      title: '5. Who we are',
      body: [REQUISITES.en],
    },
  ],
};

const supportKk: LegalDoc = {
  title: 'Қолдау',
  updated: `${UPDATED} редакциясы`,
  intro: `Бір нәрсе жұмыс істемесе немесе оқу бойынша сұрағыңыз болса, бізбен қалай байланысуға болады. Жұмыс күндері, әдетте тәулік ішінде жауап береміз.`,
  sections: [
    {
      title: '1. Қалай хабарласуға болады',
      body: [
        `• Электрондық пошта: ${CONTACT} — тіркелгіңіз жасалған мекенжайдан жазыңыз, сонда сізді тезірек табамыз.`,
      ],
    },
    {
      title: '2. Немен көмектесеміз',
      body: [
        '• Кіру коды бар хат келмейді. «Спам» қалтасын тексеріңіз; хат болмаса, бізге жазыңыз — кодты қолмен жібереміз.',
        '• Құпиясөзді ұмыттыңыз. Кіру экранында «Құпиясөзді ұмыттыңыз ба» түймесін басыңыз — ауыстыру сілтемесі келеді.',
        '• Сабақ немесе тапсырма ашылмайды. Не және қай экранда екенін жазыңыз, мүмкіндігінше экран суретін қоса жіберіңіз.',
        '• Бейне немесе дыбыс ойнатылмайды. Көбіне қолданбаны қайта орнату немесе басқа желі көмектеседі; болмаса — бізге жазыңыз.',
        '• Оқу сұрақтары: кесте, үй жұмыстары, деңгей, емтиханға дайындық.',
      ],
    },
    {
      title: '3. Тіркелгі',
      body: [
        `• Тіркелгіні ${APP_NAME} қолданбасында өзіңіз жоя аласыз: «Профиль» → «Параметрлер» → «Профильді жою». Веб-нұсқадан — ${CONTACT} мекенжайына жазыңыз. Толығырақ — «Аккаунтты жою» бетінде.`,
        '• Атыңызды, тілді және хабарландыруларды «Профиль» бөлімінде өзгертуге болады.',
      ],
    },
    {
      title: '4. Құжаттар',
      body: [
        '• Құпиялылық саясаты — /privacy',
        '• Пайдалану шарттары — /terms',
        '• Аккаунтты жою — /delete-account',
      ],
    },
    {
      title: '5. Біз кімбіз',
      body: [REQUISITES.kk],
    },
  ],
};

export const SUPPORT: Record<string, LegalDoc> = { ru: supportRu, kk: supportKk, en: supportEn };

export const DELETE_ACCOUNT: Record<string, LegalDoc> = { ru: deleteRu, kk: deleteKk, en: deleteEn };
